import { useCallback, useState } from "react";
import { useCustomInferenceCu } from "domains/3d/hooks/useCustomInferenceCu";
import { useGetBody } from "domains/3d/hooks/useGetBody";
import { useIsDisabled } from "domains/3d/hooks/useIsDisabled";
import { Form } from "domains/3d/interfaces";
import {
  getFirstFileInput,
  getFirstImage,
  getFormDefaultValue,
} from "domains/3d/utils";
import { ASSET_TYPE } from "domains/assets/utils/getIsType";
import useLastModelId from "domains/models/hooks/useLastModelId";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  usePostGenerateCustomMutation,
} from "infra/api/generated/api";
import _ from "lodash";

interface UseCustomInferenceProps {
  type: "3d" | "video" | "video-upscale";
}

export function useCustomInference({ type }: UseCustomInferenceProps) {
  const [model, setModel] = useState<
    GetModelsByModelIdApiResponse["model"] | undefined
  >(undefined);
  const handleApiError = useHandleApiError();
  const { errorToast } = useScenarioToast();
  const { selectedProject } = useTeamContext();
  const { showLimitModal } = usePlanContext();
  const [, setLastModelId] = useLastModelId(type);
  const [form, setForm] = useState<Form>({});
  const body = useGetBody({ model, form });
  const { isDisabled, reasons: disabledReasons } = useIsDisabled({
    model,
    body,
  });
  const { cuCost, isCuLoading } = useCustomInferenceCu({ model, body });

  const handleSetModel = useCallback(
    (newModel: GetModelsByModelIdApiResponse["model"]) => {
      setLastModelId(newModel.id);
      const previousModel = model;
      if (newModel.id !== previousModel?.id) {
        setModel(newModel);
        setForm((previousForm) => {
          const newForm = (newModel.inputs ?? []).reduce((acc, field) => {
            acc[field.name] = getFormDefaultValue(field);
            return acc;
          }, {} as Form);

          newModel?.inputs?.forEach((newModelInput) => {
            if (
              previousModel?.inputs?.some((previousModelInput) =>
                _.isEqual(
                  _.omit(newModelInput, [
                    "default",
                    "description",
                    "required",
                    "maxLength",
                  ]),
                  _.omit(previousModelInput, [
                    "default",
                    "description",
                    "required",
                    "maxLength",
                  ])
                )
              )
            ) {
              const previousValue = previousForm[newModelInput.name];
              if (newModelInput.maxLength !== undefined) {
                if (typeof previousValue === "string") {
                  newForm[newModelInput.name] = previousValue.slice(
                    0,
                    newModelInput.maxLength
                  );
                  return;
                } else if (
                  Array.isArray(previousValue) &&
                  previousValue.every((item) => typeof item === "string")
                ) {
                  newForm[newModelInput.name] = previousValue.map((item) =>
                    item.slice(0, newModelInput.maxLength)
                  );
                  return;
                }
              }
              newForm[newModelInput.name] = previousValue;
            }
          });

          const firstImage = getFirstImage(previousModel, previousForm);
          if (firstImage) {
            const firstFileInput = getFirstFileInput(newModel);
            if (firstFileInput) {
              newForm[firstFileInput.name] =
                firstFileInput.type === "file" ? firstImage : [firstImage];
            }
          }

          return newForm;
        });
      }
    },
    [model, setLastModelId]
  );

  const [generateCustomTrigger, { isLoading: isGenerating }] =
    usePostGenerateCustomMutation();
  const handleGenerate = useCallback(async () => {
    if (isDisabled || !model?.id) return;

    try {
      const { job } = await generateCustomTrigger({
        projectId: selectedProject.id,
        modelId: model.id,
        body,
      }).unwrap();
      return job;
    } catch (error: any) {
      const errorText = `Error generating ${
        ASSET_TYPE[type === "video-upscale" ? "video" : type].singular
      }`;
      handleApiError(error, errorText, {
        quota: () => {
          if (_.get(error, "data.details.remainingSeconds")) {
            showLimitModal("planCooldown", {
              timeout: error.data.details.remainingSeconds,
              type: type === "video-upscale" ? "video" : type,
            });
          } else if (_.get(error, "data.name") === "RateLimitError") {
            showLimitModal("planImageGenerationsRateLimit");
          } else if (
            _.get(error, "data.details.actionName") === "parallel-inferences"
          ) {
            showLimitModal("parallelInferences");
          } else if (
            _.get(error, "data.reason").includes(
              "You have reached your plan's limit."
            )
          ) {
            showLimitModal("notEnoughCreativeUnits");
          } else {
            errorToast({
              title: errorText,
              description: _.get(error, "data.reason"),
            });
          }
        },
      });
    }
  }, [
    isDisabled,
    generateCustomTrigger,
    selectedProject.id,
    body,
    handleApiError,
    showLimitModal,
    model?.id,
    type,
    errorToast,
  ]);

  return {
    handleGenerate,
    isGenerating,
    isDisabled,
    cuCost,
    isCuLoading,
    form,
    setForm,
    model,
    setModel: handleSetModel,
    disabledReasons,
  };
}
